import {
  useEffect,
  useRef,
  useState,
} from "react";
import "./styles.css";

type ItemType =
  | "eth"
  | "robinhood"
  | "bitcoin"
  | "enemy"
  | "ninjacat"
  | "manyu"
  | "bull";

type SpawnItem = {
  id: number;
  holeIndex: number;
  type: ItemType;
  hit: boolean;
};

type FloatingScore = {
  id: number;
  holeIndex: number;
  value: number;
};

const GAME_TIME = 60;

const ITEMS: Record<
  ItemType,
  {
    image: string;
    score: number;
  }
> = {
  robinhood: {
    image: "/robinhood.png",
    score: 25,
  },

  eth: {
    image: "/eth.png",
    score: 50,
  },

  bitcoin: {
    image: "/bitcoin.png",
    score: 100,
  },

  enemy: {
    image: "/enemy.png",
    score: 150,
  },

  ninjacat: {
    image: "/ninjacat.png",
    score: -100,
  },

  manyu: {
    image: "/manyu.png",
    score: -100,
  },

  bull: {
    image: "/bull.png",
    score: 500,
  },
};

const LEVEL_SETTINGS = [
  {
    level: 1,
    minItems: 1,
    maxItems: 1,
    visibleTime: 1600,
    spawnGap: 1350,
  },

  {
    level: 2,
    minItems: 1,
    maxItems: 1,
    visibleTime: 1450,
    spawnGap: 1200,
  },

  {
    level: 3,
    minItems: 1,
    maxItems: 2,
    visibleTime: 1300,
    spawnGap: 1100,
  },

  {
    level: 4,
    minItems: 1,
    maxItems: 2,
    visibleTime: 1150,
    spawnGap: 1000,
  },

  {
    level: 5,
    minItems: 2,
    maxItems: 3,
    visibleTime: 1000,
    spawnGap: 900,
  },

  {
    level: 6,
    minItems: 2,
    maxItems: 3,
    visibleTime: 850,
    spawnGap: 800,
  },
];

const getRandomInt = (
  min: number,
  max: number
) => {
  return (
    Math.floor(
      Math.random() *
        (max - min + 1)
    ) + min
  );
};

const shuffle = <T,>(
  array: T[]
) => {
  const copy = [...array];

  for (
    let i = copy.length - 1;
    i > 0;
    i--
  ) {
    const j = Math.floor(
      Math.random() * (i + 1)
    );

    [copy[i], copy[j]] = [
      copy[j],
      copy[i],
    ];
  }

  return copy;
};

export default function App() {
  const [started, setStarted] =
    useState(false);

  const [gameOver, setGameOver] =
    useState(false);

  const [score, setScore] =
    useState(0);

  const [timeLeft, setTimeLeft] =
    useState(GAME_TIME);

  const [level, setLevel] =
    useState(1);

  const [combo, setCombo] =
    useState(0);

  const [
    activeItems,
    setActiveItems,
  ] = useState<SpawnItem[]>([]);

  const [
    floatingScores,
    setFloatingScores,
  ] = useState<FloatingScore[]>(
    []
  );

  const [
    levelFlash,
    setLevelFlash,
  ] = useState(false);

  const idRef = useRef(1);

  const bullUsedRef =
    useRef(false);

  const bullTargetLevelRef =
    useRef(4);

  const spawnTimeoutRef =
    useRef<number | null>(null);

  const removeTimeoutsRef =
    useRef<number[]>([]);

  const startedRef =
    useRef(false);

  const gameOverRef =
    useRef(false);

  const levelRef =
    useRef(1);

  const timeLeftRef =
    useRef(GAME_TIME);

  useEffect(() => {
    startedRef.current =
      started;
  }, [started]);

  useEffect(() => {
    gameOverRef.current =
      gameOver;
  }, [gameOver]);

  useEffect(() => {
    levelRef.current =
      level;
  }, [level]);

  useEffect(() => {
    timeLeftRef.current =
      timeLeft;
  }, [timeLeft]);

  const clearSpawnTimer = () => {
    if (
      spawnTimeoutRef.current !==
      null
    ) {
      window.clearTimeout(
        spawnTimeoutRef.current
      );

      spawnTimeoutRef.current =
        null;
    }
  };

  const clearRemoveTimers = () => {
    removeTimeoutsRef.current.forEach(
      (timeout) => {
        window.clearTimeout(
          timeout
        );
      }
    );

    removeTimeoutsRef.current =
      [];
  };

  const clearAllTimeouts = () => {
    clearSpawnTimer();
    clearRemoveTimers();
  };

  const getRandomNormalType =
    (): ItemType => {
      const roll = Math.random();

      if (roll < 0.2) {
        return "robinhood";
      }

      if (roll < 0.4) {
        return "eth";
      }

      if (roll < 0.58) {
        return "bitcoin";
      }

      if (roll < 0.75) {
        return "enemy";
      }

      if (roll < 0.875) {
        return "ninjacat";
      }

      return "manyu";
    };

  /* ========================================
     BULL

     Bull can ONLY appear in the
     randomly selected level 4, 5 or 6.

     The level itself announces:
     BULL RUN!

     But the coin cannot actually
     appear until around 7 seconds
     into that level.
  ======================================== */

  const shouldSpawnBull = () => {
    if (bullUsedRef.current) {
      return false;
    }

    if (
      levelRef.current !==
      bullTargetLevelRef.current
    ) {
      return false;
    }

    const remaining =
      timeLeftRef.current;

    const elapsed =
      GAME_TIME - remaining;

    const secondsIntoLevel =
      elapsed % 10;

    /*
      Delay bull until the later
      part of the Bull Run level.
    */

    if (secondsIntoLevel < 7) {
      return false;
    }

    /*
      Avoid literally the last
      second of the game.
    */

    if (remaining < 2) {
      return false;
    }

    return true;
  };

  const removeItem = (
    id: number
  ) => {
    setActiveItems(
      (current) =>
        current.filter(
          (item) =>
            item.id !== id
        )
    );
  };

  const spawnWave = () => {
    if (
      !startedRef.current ||
      gameOverRef.current
    ) {
      return;
    }

    const settings =
      LEVEL_SETTINGS[
        levelRef.current - 1
      ];

    const amount =
      getRandomInt(
        settings.minItems,
        settings.maxItems
      );

    let occupiedHoles: number[] =
      [];

    setActiveItems(
      (current) => {
        occupiedHoles =
          current.map(
            (item) =>
              item.holeIndex
          );

        return current;
      }
    );

    const allHoles = [
      0, 1, 2,
      3, 4, 5,
      6, 7, 8,
    ];

    const availableHoles =
      shuffle(
        allHoles.filter(
          (hole) =>
            !occupiedHoles.includes(
              hole
            )
        )
      );

    if (
      availableHoles.length === 0
    ) {
      spawnTimeoutRef.current =
        window.setTimeout(
          spawnWave,
          settings.spawnGap
        );

      return;
    }

    const actualAmount =
      Math.min(
        amount,
        availableHoles.length
      );

    const newItems: SpawnItem[] =
      [];

    const spawnBullNow =
      shouldSpawnBull();

    for (
      let i = 0;
      i < actualAmount;
      i++
    ) {
      let type: ItemType;

      if (
        spawnBullNow &&
        i === 0
      ) {
        type = "bull";

        bullUsedRef.current =
          true;
      } else {
        type =
          getRandomNormalType();
      }

      const item: SpawnItem = {
        id: idRef.current++,
        holeIndex:
          availableHoles[i],
        type,
        hit: false,
      };

      newItems.push(item);

      let visibleTime =
        settings.visibleTime;

      /*
        Bull disappears quicker
        than normal objects.
      */

      if (type === "bull") {
        visibleTime =
          Math.max(
            500,
            settings.visibleTime *
              0.7
          );
      }

      const timeout =
        window.setTimeout(
          () => {
            removeItem(
              item.id
            );
          },
          visibleTime
        );

      removeTimeoutsRef.current.push(
        timeout
      );
    }

    setActiveItems(
      (current) => [
        ...current,
        ...newItems,
      ]
    );

    spawnTimeoutRef.current =
      window.setTimeout(
        spawnWave,
        settings.spawnGap
      );
  };

  /* ========================================
     START
  ======================================== */

  const startGame = () => {
    clearAllTimeouts();

    bullUsedRef.current =
      false;

    /*
      Each new game randomly chooses
      Level 4, 5 OR 6 as Bull Run.
    */

    bullTargetLevelRef.current =
      getRandomInt(4, 6);

    idRef.current = 1;

    levelRef.current = 1;

    timeLeftRef.current =
      GAME_TIME;

    gameOverRef.current =
      false;

    startedRef.current =
      true;

    setScore(0);

    setTimeLeft(
      GAME_TIME
    );

    setLevel(1);

    setCombo(0);

    setActiveItems([]);

    setFloatingScores([]);

    setGameOver(false);

    setLevelFlash(false);

    setStarted(true);
  };

  /* ========================================
     TIMER
  ======================================== */

  useEffect(() => {
    if (
      !started ||
      gameOver
    ) {
      return;
    }

    const timer =
      window.setInterval(
        () => {
          setTimeLeft(
            (current) => {
              if (
                current <= 1
              ) {
                window.clearInterval(
                  timer
                );

                startedRef.current =
                  false;

                gameOverRef.current =
                  true;

                setStarted(false);

                setGameOver(true);

                setActiveItems(
                  []
                );

                clearAllTimeouts();

                return 0;
              }

              const next =
                current - 1;

              timeLeftRef.current =
                next;

              return next;
            }
          );
        },
        1000
      );

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [
    started,
    gameOver,
  ]);

  /* ========================================
     LEVELS
  ======================================== */

  useEffect(() => {
    if (
      !started ||
      gameOver
    ) {
      return;
    }

    const elapsed =
      GAME_TIME - timeLeft;

    const nextLevel =
      Math.min(
        6,
        Math.floor(
          elapsed / 10
        ) + 1
      );

    if (
      nextLevel !== level
    ) {
      levelRef.current =
        nextLevel;

      setLevel(
        nextLevel
      );

      /*
        Same flash is used for normal
        level announcements AND Bull Run.

        JSX below decides what wording
        appears.
      */

      setLevelFlash(true);

      window.setTimeout(
        () => {
          setLevelFlash(
            false
          );
        },
        900
      );
    }
  }, [
    timeLeft,
    started,
    gameOver,
    level,
  ]);

  /* ========================================
     SPAWNER
  ======================================== */

  useEffect(() => {
    if (
      !started ||
      gameOver
    ) {
      return;
    }

    clearSpawnTimer();

    spawnTimeoutRef.current =
      window.setTimeout(
        spawnWave,
        300
      );

    return () => {
      clearSpawnTimer();
    };
  }, [
    started,
    gameOver,
    level,
  ]);

  /* ========================================
     WHACK
  ======================================== */

  const handleWhack = (
    item: SpawnItem
  ) => {
    if (
      item.hit ||
      !startedRef.current
    ) {
      return;
    }

    const points =
      ITEMS[item.type].score;

    setScore(
      (current) =>
        Math.max(
          0,
          current + points
        )
    );

    if (points > 0) {
      setCombo(
        (current) =>
          current + 1
      );
    } else {
      setCombo(0);
    }

    setActiveItems(
      (current) =>
        current.map(
          (activeItem) =>
            activeItem.id ===
            item.id
              ? {
                  ...activeItem,
                  hit: true,
                }
              : activeItem
        )
    );

    const floatingId =
      idRef.current++;

    setFloatingScores(
      (current) => [
        ...current,
        {
          id: floatingId,
          holeIndex:
            item.holeIndex,
          value: points,
        },
      ]
    );

    window.setTimeout(
      () => {
        setFloatingScores(
          (current) =>
            current.filter(
              (floating) =>
                floating.id !==
                floatingId
            )
        );
      },
      650
    );

    window.setTimeout(
      () => {
        removeItem(
          item.id
        );
      },
      165
    );
  };

  return (
    <div className="app">
      <div className="game-shell">

        {/* BACKGROUND */}

        <img
          className="background"
          src="/background.png"
          alt=""
          draggable={false}
        />

        {/* HUD */}

        <div className="hud-overlay">

          <div className="hud-value score-value">
            {score}
          </div>

          <div className="hud-value timer-value">
            0:
            {String(
              timeLeft
            ).padStart(
              2,
              "0"
            )}
          </div>

          <div className="hud-value level-value">
            {level}
          </div>

          <div className="hud-value combo-value">
            x{combo}
          </div>

        </div>

        {/* HOLES */}

        <div className="holes-layer">

          {Array.from({
            length: 9,
          }).map(
            (
              _,
              holeIndex
            ) => {
              const item =
                activeItems.find(
                  (
                    activeItem
                  ) =>
                    activeItem.holeIndex ===
                    holeIndex
                );

              return (
                <div
                  key={
                    holeIndex
                  }
                  className={`hole-slot hole-${holeIndex}`}
                >
                  <div className="hole-mask">

                    {item && (
                      <button
                        type="button"
                        className={`pop-item type-${item.type} ${
                          item.hit
                            ? "hit"
                            : ""
                        } ${
                          item.type ===
                          "bull"
                            ? "bull"
                            : ""
                        }`}
                        onPointerDown={(
                          event
                        ) => {
                          event.preventDefault();

                          handleWhack(
                            item
                          );
                        }}
                      >
                        <img
                          src={
                            ITEMS[
                              item.type
                            ].image
                          }
                          alt=""
                          draggable={
                            false
                          }
                        />
                      </button>
                    )}

                  </div>
                </div>
              );
            }
          )}

        </div>

        {/* FLOATING SCORES */}

        <div className="floating-score-layer">

          {floatingScores.map(
            (
              floating
            ) => (
              <div
                key={
                  floating.id
                }
                className={`floating-score floating-${floating.holeIndex} ${
                  floating.value <
                  0
                    ? "negative"
                    : "positive"
                }`}
              >
                {floating.value >
                0
                  ? `+${floating.value}`
                  : floating.value}
              </div>
            )
          )}

        </div>

        {/* ========================================
            LEVEL / BULL RUN ANNOUNCEMENT
        ======================================== */}

        {levelFlash &&
          started && (
            <div
              className={`level-flash ${
                level ===
                bullTargetLevelRef.current
                  ? "bull-level-flash"
                  : ""
              }`}
            >

              {level ===
              bullTargetLevelRef.current ? (
                <>
                  <span>
                    🐂 BULL RUN!
                  </span>

                  <small>
                    +500 COMING...
                  </small>
                </>
              ) : (
                <>
                  <span>
                    LEVEL {level}
                  </span>

                  <small>
                    FASTER!
                  </small>
                </>
              )}

            </div>
          )}

        {/* ========================================
            START SCREEN
        ======================================== */}

        {!started &&
          !gameOver && (
            <div className="start-overlay">

              <div className="start-card">

                <div className="start-kicker">
                  🥷 NINJACAT
                  ARCADE 🥷
                </div>

                <h1>
                  CRYPTO
                  <span>
                    {" "}
                    WHACK!
                  </span>
                </h1>

                <div className="start-rule">
                  WHACK THE
                  CRYPTO!
                </div>

                <div className="start-icons">

                  <div className="start-icon">
                    <img
                      src="/robinhood.png"
                      alt=""
                    />

                    <span>
                      +25
                    </span>
                  </div>

                  <div className="start-icon">
                    <img
                      src="/eth.png"
                      alt=""
                    />

                    <span>
                      +50
                    </span>
                  </div>

                  <div className="start-icon">
                    <img
                      src="/bitcoin.png"
                      alt=""
                    />

                    <span>
                      +100
                    </span>
                  </div>

                  <div className="start-icon enemy-start">
                    <img
                      src="/enemy.png"
                      alt=""
                    />

                    <span>
                      +150
                    </span>
                  </div>

                </div>

                <div className="avoid-title">
                  ❌ DON'T WHACK
                  THESE! ❌
                </div>

                <div className="avoid-row">

                  <div>
                    <img
                      src="/ninjacat.png"
                      alt=""
                    />

                    <span>
                      -100
                    </span>
                  </div>

                  <div>
                    <img
                      src="/manyu.png"
                      alt=""
                    />

                    <span>
                      -100
                    </span>
                  </div>

                </div>

                <div className="bull-alert">

                  <img
                    src="/bull.png"
                    alt=""
                  />

                  <div>
                    <strong>
                      LOOK OUT FOR
                      THE BULL!
                    </strong>

                    <span>
                      RARE BONUS
                      • +500
                    </span>
                  </div>

                </div>

                <div className="start-small">
                  60 SECONDS •
                  6 LEVELS •
                  GETS FASTER!
                </div>

                <button
                  type="button"
                  className="start-button"
                  onClick={
                    startGame
                  }
                >
                  WHACK! 💥
                </button>

              </div>
            </div>
          )}

        {/* ========================================
            GAME OVER
        ======================================== */}

        {gameOver && (
          <div className="start-overlay">

            <div className="start-card game-over-card">

              <div className="start-kicker">
                🥷 NINJACAT
                ARCADE 🥷
              </div>

              <h1>
                TIME!
              </h1>

              <p className="final-label">
                FINAL SCORE
              </p>

              <div className="final-score">
                {score}
              </div>

              <button
                type="button"
                className="start-button"
                onClick={
                  startGame
                }
              >
                PLAY AGAIN
              </button>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}
